import { forwardRef } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

export const MAX_UPLOAD_MB = 20;
export const ATTACHMENT_EXT = ["pdf", "doc", "docx", "xlsx", "xls", "csv", "tsv", "png", "jpg", "jpeg", "gif", "webp", "txt", "ppt", "pptx", "msg", "eml", "zip"];
export const ATTACHMENT_ACCEPT = ATTACHMENT_EXT.map((e) => `.${e}`).join(",");
export const ATTACHMENT_HINT = `PDF, Word, Excel (.xlsx/.xls), CSV/TSV, PowerPoint, images · max ${MAX_UPLOAD_MB} MB`;
export const IMPORT_EXT = ["xlsx", "xls", "csv", "tsv"];
export const IMPORT_ACCEPT = ".xlsx,.xls,.csv,.tsv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,text/tab-separated-values";
export const IMPORT_HINT = `.xlsx, .xls (incl. Bitrix HTML exports), .csv, .tsv or .html tables · max ${MAX_UPLOAD_MB} MB`;
export const SPREADSHEET_EXT = ["xlsx", "xls", "csv", "tsv"];

export const extOf = (name = "") => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");
export const isSpreadsheet = (name) => SPREADSHEET_EXT.includes(extOf(name));

// Client-side pre-check; the server re-validates real format and size
export const checkFile = (file, allowed = ATTACHMENT_EXT) => {
  if (!file) return false;
  const ext = extOf(file.name);
  if (!allowed.includes(ext)) { toast.error(`'.${ext || "?"}' files are not supported. Supported: ${allowed.map((e) => "." + e).join(", ")}`); return false; }
  if (file.size === 0) { toast.error(`'${file.name}' is empty (0 bytes)`); return false; }
  if (file.size > MAX_UPLOAD_MB * 1048576) { toast.error(`'${file.name}' is ${(file.size / 1048576).toFixed(1)} MB — the maximum is ${MAX_UPLOAD_MB} MB`); return false; }
  return true;
};

export const uploadProgress = (setPct) => ({ headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: (e) => setPct?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0) });

export const UploadProgress = ({ pct, testid = "upload-progress" }) => pct == null ? null : (
  <div className="w-full" data-testid={testid}><div className="h-1.5 rounded bg-slate-200 overflow-hidden"><div className="h-full bg-teal-600 transition-[width]" style={{ width: `${pct}%` }} /></div><div className="text-[11px] text-slate-500 mt-0.5">{pct < 100 ? `Uploading… ${pct}%` : "Saving…"}</div></div>
);

export const FileInput = forwardRef(({ onFile, testid, className = "", hint = ATTACHMENT_HINT, accept = ATTACHMENT_ACCEPT, allowed = ATTACHMENT_EXT, multiple, ...rest }, ref) => (
  <div>
    <Input ref={ref} data-testid={testid} type="file" accept={accept} multiple={multiple} className={`h-9 text-xs file:mr-3 file:text-xs ${className}`}
      onChange={(e) => {
        if (multiple) { const list = Array.from(e.target.files || []); if (list.some((f) => !checkFile(f, allowed))) { e.target.value = ""; onFile([]); return; } onFile(list); return; }
        const f = e.target.files?.[0] || null; if (f && !checkFile(f, allowed)) { e.target.value = ""; onFile(null); return; } onFile(f);
      }} {...rest} />
    <div className="text-[11px] text-slate-400 mt-1" data-testid={testid ? `${testid}-hint` : undefined}>{hint}</div>
  </div>
));
FileInput.displayName = "FileInput";
